import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Shield, Monitor, Cpu, ChevronRight, X, ChevronLeft } from 'lucide-react';

const ArchitectureSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showExperience, setShowExperience] = useState(false);
  const [experienceStep, setExperienceStep] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Body scroll lock and Escape key handler
  useEffect(() => {
    if (showExperience) {
      document.body.style.overflow = 'hidden';
      
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          setShowExperience(false);
        }
      };
      
      document.addEventListener('keydown', handleEscape);
      
      return () => {
        document.body.style.overflow = '';
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [showExperience]);

  useEffect(() => {
    if (!showExperience) {
      setExperienceStep(0);
    }
  }, [showExperience]);

  const handleNextStep = () => {
    if (experienceStep < 6) {
      setExperienceStep(prev => prev + 1);
    }
  };

  const handlePrevStep = () => {
    if (experienceStep > 0) {
      setExperienceStep(prev => prev - 1);
    }
  };

  const features = [
    'No direct connection between AI and your local system',
    'Environment isolated from open internet',
    'All sessions sandboxed and controlled',
    'AI never touches your device directly'
  ];

  return (
    <section 
      className="architecture-section" 
      id="solution"
      ref={sectionRef}
      data-testid="architecture-section"
    >
      <div className="architecture-content">
        <div className="architecture-layout">
          {/* LEFT: Text Content */}
          <div className={`architecture-left ${isVisible ? 'visible' : ''}`}>
            <span className="section-number">02</span>
            <span className="section-label-new">The Solution</span>
            <h2 className="section-title-new">Zyber Secure</h2>
            <p className="architecture-subtitle">
              Your computer should never be the attack surface. Zyber Secure creates a hardened 
              virtual layer between you and AI services, ensuring that no malicious code, data breach, 
              or compromised package can ever reach your actual device. Work with AI fearlessly.
              We absorb all the risk.
            </p>
            
            <div className="architecture-features">
              {features.map((feature, index) => (
                <div key={index} className="feature-item">
                  <span className="feature-check">✓</span>
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            <button 
              className="experience-btn"
              onClick={() => setShowExperience(true)}
              data-testid="experience-btn"
            >
              <span>Experience Zyber Secure</span>
              <ChevronRight size={18} />
            </button>
          </div>

          {/* RIGHT: Flowchart */}
          <div className={`architecture-right ${isVisible ? 'visible' : ''}`}>
            <div className="flowchart-visual">
              <div className="flow-node">
                <div className="node-icon-wrap">
                  <Monitor size={26} />
                </div>
                <div className="node-text">
                  <span className="node-label">Your Device</span>
                  <span className="node-desc">Stays private & protected</span>
                </div>
              </div>

              <div className="flow-connection"></div>

              <div className="flow-node center zyber-node">
                <div className="zyber-node-glow"></div>
                <img src="/brand/mark-3d.png" alt="Zyber Secure" className="zyber-node-mark" />
                <div className="node-text">
                  <span className="node-label">Zyber Secure</span>
                  <span className="node-desc">Hardened sandbox layer</span>
                </div>
              </div>

              <div className="flow-connection"></div>

              <div className="flow-node">
                <div className="node-icon-wrap">
                  <Cpu size={26} />
                </div>
                <div className="node-text">
                  <span className="node-label">AI Providers</span>
                  <span className="node-desc">Chat, code & tools</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Experience Modal */}
      {showExperience && ReactDOM.createPortal(
        <div className="experience-modal-fixed" onClick={() => setShowExperience(false)} data-testid="experience-modal">
          <div className="experience-content-fixed" onClick={(e) => e.stopPropagation()}>
            <button className="experience-close" onClick={() => setShowExperience(false)} data-testid="experience-close-btn">
              <X size={20} />
            </button>
            
            <div className="browser-mockup-fixed">
              <div className="browser-titlebar">
                <div className="browser-controls">
                  <span style={{background: '#ff5f56'}}></span>
                  <span style={{background: '#ffbd2e'}}></span>
                  <span style={{background: '#27c93f'}}></span>
                </div>
                <div className="browser-tab">Zyber Secure</div>
              </div>
              
              <div className="browser-addressbar">
                <div className="url-bar">
                  <span>🔒 secure.zybersec.com</span>
                </div>
              </div>

              <div className="browser-content">
                {experienceStep === 0 && (
                  <div className="exp-screen">
                    <div className="exp-icon">
                      <Shield size={48} />
                    </div>
                    <h4 className="screen-title">Create Your Account</h4>
                    <p className="screen-subtitle">Join Zyber Secure to protect your AI interactions</p>
                  </div>
                )}

                {experienceStep === 1 && (
                  <div className="exp-screen">
                    <h4 className="screen-title">Choose Your Plan</h4>
                    <p className="screen-subtitle">Select the usage tier that fits your needs</p>
                    <div className="exp-plans">
                      <div className="exp-plan">Starter $5/mo</div>
                      <div className="exp-plan active">Pro $15/mo</div>
                      <div className="exp-plan">Enterprise $29/mo</div>
                    </div>
                  </div>
                )}

                {experienceStep === 2 && (
                  <div className="exp-screen">
                    <div className="exp-loading">
                      <div className="exp-spinner"></div>
                    </div>
                    <h4 className="screen-title green">Initializing VM</h4>
                    <p className="screen-subtitle">Setting up secure environment...</p>
                  </div>
                )}

                {experienceStep === 3 && (
                  <div className="exp-screen">
                    <h4 className="screen-title">Connect AI Services</h4>
                    <div className="exp-services">
                      <div className="exp-service connected">GPT Connected</div>
                      <div className="exp-service">Claude</div>
                      <div className="exp-service">Gemini</div>
                    </div>
                  </div>
                )}

                {experienceStep === 4 && (
                  <div className="exp-screen">
                    <h4 className="screen-title">Using AI Securely</h4>
                    <div className="exp-chat">
                      <div className="exp-msg user">Help me build a game in Python</div>
                      <div className="exp-msg ai">Installing packages...</div>
                      <div className="exp-msg warning">⚠️ Suspicious package detected</div>
                    </div>
                  </div>
                )}

                {experienceStep === 5 && (
                  <div className="exp-screen danger">
                    <div className="exp-icon danger">⚠️</div>
                    <h4 className="screen-title danger">THREAT DETECTED</h4>
                    <p className="screen-subtitle">Malicious package: game-utils-v2</p>
                    <p className="screen-subtitle">Attempting data exfiltration...</p>
                  </div>
                )}

                {experienceStep === 6 && (
                  <div className="exp-screen success">
                    <div className="exp-icon success">
                      <Shield size={48} />
                    </div>
                    <h4 className="screen-title success">ATTACK NEUTRALIZED</h4>
                    <p className="screen-subtitle">Your device remains completely safe</p>
                    <div className="exp-stats">
                      <div className="exp-stat">0 Files Compromised</div>
                      <div className="exp-stat">100% Protected</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="experience-nav">
              <div className="nav-dots">
                {[0, 1, 2, 3, 4, 5, 6].map((index) => (
                  <button 
                    key={index}
                    className={`nav-dot ${index === experienceStep ? 'active' : ''}`}
                    onClick={() => setExperienceStep(index)}
                  />
                ))}
              </div>

              <div className="nav-buttons-row">
                <button 
                  className="nav-btn"
                  onClick={handlePrevStep}
                  disabled={experienceStep === 0}
                >
                  <ChevronLeft size={16} />
                  <span>Back</span>
                </button>

                <span className="step-counter">{experienceStep + 1} / 7</span>

                {experienceStep < 6 ? (
                  <button className="nav-btn" onClick={handleNextStep}>
                    <span>Next</span>
                    <ChevronRight size={16} />
                  </button>
                ) : (
                  <button className="nav-btn" onClick={() => setExperienceStep(0)}>
                    <span>Restart</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
};

export default ArchitectureSection;
